if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector('[data-mh-buy-label]') || this.submitButton.querySelector('span');
        this.defaultSubmitButtonText = this.submitButtonText?.textContent.trim();
        this.cartFeedback = this.querySelector('[data-mh-cart-feedback]');
        // The phone bottom bar proxies clicks to this form; mirror the pending/added state onto it.
        this.mirrorButtons = Array.from(this.closest('.mh-pdp2')?.querySelectorAll('[data-mh-mobile-submit]') || []);

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.hideSuccessState();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.mirrorButtons.forEach((button) => { button.setAttribute('aria-disabled', 'true'); button.classList.add('loading'); });
        this.querySelector('.loading__spinner').classList.remove('hidden');
        // perf plan B2: the pending state stays visible ≥ 300 ms (no spinner flash on fast responses), the header
        // badge counts up immediately (reverted on error, replaced by the real count when the sections re-render),
        // and the page-level progress bar is told the page is busy.
        this.pendingSince = Date.now();
        this.bumpCartBadge(parseInt(new FormData(this.form).get('quantity')) || 1);
        document.dispatchEvent(new CustomEvent('mh:busy', { detail: { source: 'product-form' } }));

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));
              this.revertCartBadge();

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.renderCartContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
              this.showSuccessState();
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.renderCartContents(response);
              });
              this.showSuccessState();
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
            this.revertCartBadge();
          })
          .finally(() => {
            const settle = () => {
              this.submitButton.classList.remove('loading');
              this.mirrorButtons.forEach((button) => { button.classList.remove('loading'); if (!this.error) button.removeAttribute('aria-disabled'); });
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');
              document.dispatchEvent(new CustomEvent('mh:idle', { detail: { source: 'product-form' } }));
            };
            const remaining = 300 - (Date.now() - (this.pendingSince || 0));
            if (remaining > 0) setTimeout(settle, remaining);
            else settle();

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      cartBadge() {
        const link = document.getElementById('cart-icon-bubble');
        if (!link) return null;
        return link.querySelector('.mh-badge') || link.querySelector('.cart-count-bubble span[aria-hidden]');
      }

      bumpCartBadge(quantity) {
        const link = document.getElementById('cart-icon-bubble');
        if (!link) return;
        let badge = this.cartBadge();
        this.badgeBefore = badge ? { node: badge, text: badge.textContent } : { node: null, text: null };
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'mh-badge';
          badge.dataset.mhOptimistic = '';
          link.appendChild(badge);
        }
        const current = parseInt(badge.textContent) || 0;
        badge.textContent = String(current + quantity);
      }

      revertCartBadge() {
        const before = this.badgeBefore;
        if (!before) return;
        const link = document.getElementById('cart-icon-bubble');
        const created = link?.querySelector('.mh-badge[data-mh-optimistic]');
        if (created) created.remove();
        else if (before.node && before.node.isConnected) before.node.textContent = before.text;
        this.badgeBefore = null;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      renderCartContents(response) {
        const renderOptions = this.cart.matches('cart-drawer') ? { open: false } : undefined;
        this.cart.renderContents(response, renderOptions);
      }

      showSuccessState() {
        const addedLabel = window.cartStrings?.addedShort;
        if (this.submitButtonText && addedLabel) this.submitButtonText.textContent = addedLabel;
        this.submitButton.classList.add('is-added');
        this.mirrorButtons.forEach((button) => {
          button.classList.add('is-added');
          const label = button.querySelector('[data-mh-mobile-label]');
          if (label && addedLabel) { label.dataset.mhRestoreLabel = label.textContent.trim(); label.textContent = addedLabel; }
        });
        if (this.cartFeedback) {
          this.cartFeedback.hidden = false;
          // The inline feedback sits in the buy panel; when the fixed phone bar was used it is off-screen, so also toast.
          if (this.mirrorButtons.length && window.matchMedia('(max-width: 989px)').matches) this.showCartToast();
        } else {
          this.showCartToast();
        }

        clearTimeout(this.successStateTimer);
        this.successStateTimer = setTimeout(() => {
          this.submitButton.classList.remove('is-added');
          if (this.submitButtonText && this.defaultSubmitButtonText) {
            this.submitButtonText.textContent = this.defaultSubmitButtonText;
          }
          this.mirrorButtons.forEach((button) => {
            button.classList.remove('is-added');
            const label = button.querySelector('[data-mh-mobile-label]');
            if (label?.dataset.mhRestoreLabel) { label.textContent = label.dataset.mhRestoreLabel; delete label.dataset.mhRestoreLabel; }
          });
        }, 1800);
      }

      showCartToast() {
        let toast = document.querySelector('[data-product-form-cart-toast]');
        if (!toast) {
          toast = document.createElement('div');
          toast.className = 'product-form__cart-toast';
          toast.dataset.productFormCartToast = '';
          toast.setAttribute('role', 'status');
          toast.setAttribute('aria-live', 'polite');
          toast.setAttribute('aria-atomic', 'true');

          const message = document.createElement('span');
          const link = document.createElement('a');
          message.dataset.productFormCartToastMessage = '';
          link.href = window.routes.cart_url;
          link.textContent = window.cartStrings?.viewCart;
          toast.append(message, link);
          document.body.append(toast);
        }

        const message = toast.querySelector('[data-product-form-cart-toast-message]');
        if (message) message.textContent = window.cartStrings?.addedToCart;
        toast.hidden = false;
        clearTimeout(toast.hideTimer);
        toast.hideTimer = setTimeout(() => {
          toast.hidden = true;
        }, 4000);
      }

      hideSuccessState() {
        clearTimeout(this.successStateTimer);
        this.submitButton.classList.remove('is-added');
        if (this.submitButtonText && this.defaultSubmitButtonText) {
          this.submitButtonText.textContent = this.defaultSubmitButtonText;
        }
        if (this.cartFeedback) this.cartFeedback.hidden = true;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
